import { Grid, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import ProfileDataCard from "./ProfileDataCard";

interface StudyDetailsProps {
  enrolledDegree: string;
  enrolledUniversity: string;
  enrolledSubject: string | null;
  studyStart: Date | null;
  studySemester: string | null;
  studyFirstMajor: string | null;
  studySecondMajor: string | null;
  studyThirdMajor: string | null;
  bachelorSubject: string | null;
  bachelorUniversity: string | null;
}

/**
 * This component displays the study details of an applicant
 * @param enrolledDegree The enrolled degree of the applicant
 * @param enrolledUniversity The enrolled university of the applicant
 * @param enrolledSubject The enrolled subject of the applicant
 * @param studyStart The start date of the study
 * @param studySemester The current semester of the study
 * @param studyFirstMajor The first major of the study
 * @param studySecondMajor The second major of the study
 * @param studyThirdMajor The third major of the study
 * @param bachelorSubject The bachelor subject of the applicant
 * @param bachelorUniversity The bachelor university of the applicant
 * @returns The StudyDetails component
 */
const StudyDetails = ({
  enrolledDegree,
  enrolledUniversity,
  enrolledSubject,
  studyStart,
  studySemester,
  studyFirstMajor,
  studySecondMajor,
  studyThirdMajor,
  bachelorSubject,
  bachelorUniversity,
}: StudyDetailsProps) => {
  const theme = useTheme();

  const majorSubjects = [studyFirstMajor, studySecondMajor, studyThirdMajor]
    .filter((major) => major !== null)
    .map((major) => major)
    .join(", ");

  return (
    <ProfileDataCard title={`Studium (${enrolledDegree})`}>
      <Grid container sx={{ paddingLeft: 1, paddingRight: 1 }} rowSpacing={2} columnSpacing={1}>
        <Grid container item xs={12} sm={7} rowGap={1}>
          <Grid item xs={12}>
            <Typography fontWeight={"bold"} color={theme.palette.info.main}>
              Studiengang
            </Typography>
            <Typography>{enrolledSubject}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography fontWeight={"bold"} color={theme.palette.info.main}>
              Hochschule
            </Typography>
            <Typography>{enrolledUniversity}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography fontWeight={"bold"} color={theme.palette.info.main}>
              Studienbeginn
            </Typography>
            <Typography>{dayjs(studyStart).format("MMMM YYYY")}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography fontWeight={"bold"} color={theme.palette.info.main}>
              Fachsemester
            </Typography>
            <Typography>{studySemester}</Typography>
          </Grid>
          <Grid item xs>
            <Typography fontWeight={"bold"} color={theme.palette.info.main}>
              Vertiefungen
            </Typography>
            <Typography>{majorSubjects}</Typography>
          </Grid>
        </Grid>
        {enrolledDegree === "Master" ? (
          <Grid item xs={12} sm={5}>
            <Typography fontWeight={"bold"} fontSize={18}>
              Bachelorabschluss
            </Typography>
            <Typography fontWeight={"bold"} color={theme.palette.info.main}>
              Studiengang
            </Typography>
            <Typography>{bachelorSubject}</Typography>
            <Typography fontWeight={"bold"} color={theme.palette.info.main} sx={{ marginTop: 1 }}>
              Hochschule
            </Typography>
            <Typography>{bachelorUniversity}</Typography>
          </Grid>
        ) : null}
      </Grid>
    </ProfileDataCard>
  );
};

export default StudyDetails;
